import { Box, Modal, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import TextButton from "../../../../../../../components/buttons/TextButton";
import { Toast } from "../../../../../../../components/Toast";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import {
  getPrettyDateTime,
  useLocalTime,
} from "../../../../../../../utils/timestamp";
import { ReactComponent as CloseIcon } from "../../../../../../stack/environment/icons/close_icon.svg";
import { resetQueriesAfterAutoRemediation } from "./utils";

export default function UndoModal({ showUndoModal, setShowUndoModal, log }) {
  const colors = useColors();
  const { rollbackAutoRemediation } = useAPI();
  const showLocalTime = useLocalTime();
  const queryClient = useQueryClient();

  const rollbackMutation = useMutation({
    mutationFn: () => rollbackAutoRemediation(log.remediation_id),
    onSuccess: () => {
      resetQueriesAfterAutoRemediation(queryClient, log.remediation_id);
      Toast.success("Remediation rolled back successfully");
      setShowUndoModal(false);
    },
    onError: () => {
      Toast.error("Failed to rollback remediation");
    },
  });

  const handleClose = () => {
    if (!rollbackMutation.isPending) setShowUndoModal(false);
  };

  return (
    <Modal open={showUndoModal} onClose={handleClose}>
      <Box
        padding="24px"
        position="absolute"
        borderRadius="10px"
        top="50%"
        left="50%"
        sx={{
          transform: "translate(-50%, -50%)",
          backgroundColor: colors.modal_bg,
          outline: "none",
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" color={colors.text_primary}>
              Undo Applied Remediation
            </Typography>
            <Box
              height="18px"
              onClick={handleClose}
              sx={{ cursor: !rollbackMutation.isPending ? "pointer" : "auto" }}
            >
              <SvgToIcon
                IconLogo={CloseIcon}
                width="18px"
                style={{ color: colors.action_active }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color={colors.text_primary}>
              This will revert the most recent auto-remediation applied to the
              selected entities.
            </Typography>
            <Typography variant="body2" color={colors.text_secondary}>
              Affected entities: {log.remediated_entities_count}
              <br />
              Applied by: {log.user_display_name}
              <br />
              Applied on:{" "}
              {getPrettyDateTime(
                log.log_timestamp.timestamp_utc,
                showLocalTime
              )}
            </Typography>
            <Typography variant="body2" color={colors.text_primary}>
              You can reapply the remediation later to any or all entities.
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <TextButton
                onClick={handleClose}
                color="inherit"
                size="small"
                disabled={rollbackMutation.isPending}
              >
                Cancel
              </TextButton>
              <TextButton
                onClick={() => rollbackMutation.mutate()}
                color="error"
                size="small"
                loading={rollbackMutation.isPending}
              >
                Undo Fix
              </TextButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
